import { useEffect, useRef, useState } from "react";
import DailyIframe from "@daily-co/daily-js";
import { Badge } from "@/components/ui/badge";
import { Circle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface DailyVideoCallProps {
  roomUrl: string;
  token: string;
  meetingId?: string;
  onMeetingStarted?: () => void;
  onMeetingEnded?: () => void;
  onRecordingStarted?: () => void;
}

export function DailyVideoCall({
  roomUrl, token, meetingId,
  onMeetingStarted, onMeetingEnded, onRecordingStarted,
}: DailyVideoCallProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const callFrameRef = useRef<any>(null);
  const autoStartRequestedRef = useRef(false);
  const lastAutoStartAtRef = useRef(0);
  const meetingIdRef = useRef<string | undefined>(meetingId);
  const isRecordingRef = useRef(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isReady, setIsReady] = useState(false);

  // Keep ref synced so handlers see latest value without recreating frame
  useEffect(() => { meetingIdRef.current = meetingId; }, [meetingId]);

  // Effect 1: Delay init to skip React strict mode double-mount
  useEffect(() => {
    const timer = setTimeout(() => setIsReady(true), 100);
    return () => {
      clearTimeout(timer);
      setIsReady(false);
      autoStartRequestedRef.current = false;
      lastAutoStartAtRef.current = 0;
      try { callFrameRef.current?.destroy(); } catch (e) {}
      callFrameRef.current = null;
      try { DailyIframe.getCallInstance()?.destroy(); } catch (e) {}
    };
  }, []);

  // Effect 2: Create frame only after ready
  useEffect(() => {
    if (!isReady || !containerRef.current || !roomUrl || !token) return;
    if (callFrameRef.current) return;

    try {
      const existing = DailyIframe.getCallInstance();
      if (existing) existing.destroy();
    } catch (e) {}

    const frame = DailyIframe.createFrame(containerRef.current, {
      iframeStyle: { width: "100%", height: "100%", border: "0", borderRadius: "12px" },
      showLeaveButton: true,
      showFullscreenButton: true,
      lang: "pt",
    });

    callFrameRef.current = frame;

    const logRecordingFailure = async (attempts: Array<{ attempt: number; error: string }>) => {
      const id = meetingIdRef.current;
      if (!id) return;
      try {
        // audit_logs has a metadata jsonb column → safe place to record
        // recording auto-start failures without changing meetings schema.
        await supabase.from("audit_logs").insert({
          entity_type: "meeting",
          entity_id: id,
          action_type: "recording_auto_start_failed",
          field_changed: "recorded",
          new_value: "false",
          metadata: {
            attempts,
            failed_at: new Date().toISOString(),
            attempts_count: attempts.length,
          },
        });
      } catch (err) {
        console.warn("Failed to log recording failure:", err);
      }
    };

    const tryStartRecordingOnce = async (): Promise<{ ok: boolean; error?: string }> => {
      try {
        await Promise.resolve(frame.startRecording?.({ width: 640, height: 360 }));
        return { ok: true };
      } catch (err: any) {
        const message = String(err?.message || err || "");
        const lower = message.toLowerCase();
        // "already recording" counts as success
        if (lower.includes("already")) return { ok: true };
        return { ok: false, error: message };
      }
    };

    const requestAutoStartRecording = async () => {
      const now = Date.now();
      if (now - lastAutoStartAtRef.current < 1500) return;
      lastAutoStartAtRef.current = now;

      // 3 retries with exponential backoff: immediate, 1s, 3s, 7s
      const delays = [0, 1000, 3000, 7000];
      const attempts: Array<{ attempt: number; error: string }> = [];

      for (let i = 0; i < delays.length; i++) {
        if (delays[i] > 0) {
          await new Promise((r) => setTimeout(r, delays[i]));
        }
        // If recording started in the meantime (event fired), stop trying
        if (isRecordingRef.current) return;

        const result = await tryStartRecordingOnce();
        if (result.ok) {
          // Wait a moment to confirm recording-started event fired
          await new Promise((r) => setTimeout(r, 1500));
          if (isRecordingRef.current) return;
          attempts.push({ attempt: i + 1, error: "started but no confirmation event" });
        } else {
          attempts.push({ attempt: i + 1, error: result.error || "unknown" });
        }
      }

      // All attempts failed → notify user + log to DB
      console.error("Auto-start recording failed after 4 attempts:", attempts);
      toast.error("⚠️ Gravação não iniciou. Clique no ícone de gravar manualmente.", {
        duration: 10000,
      });
      logRecordingFailure(attempts);
    };

    const markStatus = (status: "in_progress" | "completed") => {
      const id = meetingIdRef.current;
      if (!id) return;
      supabase.functions.invoke("mark-meeting-status", {
        body: { meeting_id: id, status },
      }).catch((err) => {
        console.warn("mark-meeting-status failed:", err);
      });
    };

    frame.on("joined-meeting", () => {
      onMeetingStarted?.();
      markStatus("in_progress");
      if (!autoStartRequestedRef.current) {
        autoStartRequestedRef.current = true;
        // Fallback: if server-side auto-start did not trigger yet, request recording from host client.
        requestAutoStartRecording();
      }
    });
    frame.on("left-meeting", () => {
      // NÃO marcamos "completed" aqui: o frontend não sabe se outros participantes
      // ainda estão na sala. Quem decide o fim é o webhook do Daily (meeting.ended,
      // disparado quando a sala fica vazia) — respeitando a regra de reentrada
      // para reuniões agendadas até o end_at do calendar_event.
      onMeetingEnded?.();
    });
    frame.on("recording-started", () => {
      isRecordingRef.current = true;
      setIsRecording(true);
      onRecordingStarted?.();
    });
    frame.on("recording-stopped", () => {
      isRecordingRef.current = false;
      setIsRecording(false);
      // Enforce "always recording" behavior if someone stops it manually.
      setTimeout(() => requestAutoStartRecording(), 500);
    });
    frame.join({ url: roomUrl, token }).catch((err: any) => {
      console.error("Daily.co join error:", err);
    });

    return () => {
      autoStartRequestedRef.current = false;
      lastAutoStartAtRef.current = 0;
      try { frame.destroy(); } catch (e) {}
      callFrameRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady, roomUrl, token]);

  return (
    <div className="relative w-full h-full">
      {isRecording && (
        <Badge variant="destructive" className="absolute top-3 left-3 z-10 flex items-center gap-1.5 animate-pulse">
          <Circle className="w-2 h-2 fill-current" />
          Gravando
        </Badge>
      )}
      <div ref={containerRef} className="w-full h-full" />
    </div>
  );
}
